import React, { useState } from "react";
import ScoreBoard from "./components/ScoreBoard";
import ScoreHistoryPanel from "./components/ScoreHistoryPanel";
import Gumball from "../gumball/Gumball";
import GumballHistoryPage from "../components/GumballHistoryPage";
import StreakDetail from "./components/StreakDetail";
import ProgressBar from "./components/ProgressBar";
import Celebration from "./components/Celebration";
import StreakModal from "./components/StreakModal";
import ProgressGraphModal from "./components/ProgressGraphModal";
import LeaderboardModal from "./components/LeaderboardModal";
import ScorecardModal from "./components/ScorecardModal";
import { usePDActiveSubscription } from '../../purpledoor/hooks/pdActiveSubscription';

const ANIMAL_EMOJI = { egg: "🥚", hatchling: "🐣", fox: "🦊", owl: "🦉", lion: "🦁", dragon: "🐉" };
const THEME_ICON = {
  Animals: "🐶", Fruits: "🍎", Vehicles: "🚗", Colours: "🎨",
  Numbers: "🔢", Family: "👨‍👩‍👧", Nature: "🌎",
};

export default function TinyThinkHome(
  { theme, streakInfo, history, bestRecord, leaderboard, fetchLeaderboard, loading, onStart, soundEnabled, onToggleSound, todayCompleted, 
    onCheckScorecard, onCheckStreaks, onViewProgress, onCheckLeaderboard, onOpenGumball  
  }) {
  const [showHistory, setShowHistory] = useState(false);
  const [activeModal, setActiveModal] = useState(null); 
  const openModal = (modalName) => setActiveModal(modalName);
  const closeModal = () => setActiveModal(null);

  const {
        subscriptions,
        selectedSubscription,
        loadingSubs,
        product,
        isActive,
        daysLeft,
        formatRange,
        refetch
    } = usePDActiveSubscription();
  
    if (loadingSubs) return <p>Loading subscription...</p>;
  

  return (
    <div className="min-h-screen bg-[#21076e] text-gray-200 pb-24">
      <header className="bg-[#7C3AED] text-white px-4 pt-2 pb-4 rounded-b-3xl text-center relative">
        <button
          onClick={onToggleSound}
          className="absolute top-4 left-4 w-fit px-2 py-1 text-lg lg:text-xl text-white opacity-90"
          aria-label={soundEnabled ? "Mute sound" : "Enable sound"}
        >
          {soundEnabled ? "🔊" : "🔇"}
        </button>
        <p className="text-xs uppercase tracking-wider opacity-80 font-semibold text-cyan-200">
          PurpleDoor TinyThink
        </p>
        <h1 className="text-2xl sm:text-3xl text-yellow-200 font-extrabold mt-1">
          5 Minutes. 5 Levels.
        </h1>
        <p className="text-sm opacity-90 mt-0.5 text-gray-100">
          One Happy Family. 🧸
        </p>

        <div className="mt-4 inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1.5">
          <span className="text-xl" aria-hidden="true">
            {ANIMAL_EMOJI[streakInfo?.animalStage || "egg"]}
          </span>
          <span className="text-sm font-semibold">
            {streakInfo?.currentStreak || 0} day streak
          </span>
        </div>
      </header>

      <main className="px-4 -mt-4 space-y-4 max-w-md mx-auto sm:max-w-lg">
        <div className="bg-blue-800 text-white rounded-2xl shadow-md p-4 text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl" aria-hidden="true">
              {THEME_ICON[theme] || "🌟"}
            </span>
            <span className="text-sm font-bold text-yellow-200">
              Today's theme: {theme}{" "}
              {todayCompleted ? "complete" : "not yet completed"}
            </span>
          </div>

          <div className="mt-4 flex flex-col items-center">
            {isActive ? (
              <div className="text-center bg-green-900/30 border border-green-500/50 rounded-2xl px-6 py-4 max-w-md">
                <div className="flex items-center justify-center gap-2 text-green-400 mb-1">
                  <span className="text-lg lg:text-xl">🎉</span>
                  <span className="font-semibold text-lg">
                    Active Subscription
                  </span>
                </div>
                <p className="text-green-300 text-xs lg:text-sm">
                  Days Left:{" "}
                  <span className="font-bold text-sm lg:text-lg text-white">
                    {daysLeft}
                  </span>
                </p>
              </div>
            ) : (
              <div className="text-center bg-red-900/30 border border-red-500/50 rounded-2xl px-6 py-4 max-w-md">
                <div className="flex items-center justify-center gap-2 text-red-400 mb-1">
                  <span className="text-lg lg:text-xl">⚠️</span>
                  <span className="font-semibold text-sm lg:text-lg">
                    No Active Subscription
                  </span>
                </div>
                <p className="text-red-300 text-sm lg:text-lg mt-1">
                  Your subscription has expired or is inactive
                </p>
              </div>
            )}
          </div>

          <div className="bg-blue-700 rounded-2xl shadow-md p-4 space-y-3">
            {todayCompleted && (
              <div className="text-center">
                <div className="text-4xl mb-1" aria-hidden="true">
                  🎊
                </div>
                <p className="text-base font-extrabold text-white">
                  You crushed it today!
                </p>
                <p className="text-xs text-gray-100 mt-0.5">
                  Come back tomorrow for a new theme. For now, check this out:
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={() => openModal("scorecard")}
                className="flex flex-col items-center gap-1.5 bg-[#FFF4E8] hover:bg-[#FFE9CE] rounded-2xl py-4 px-2 transition-colors active:scale-95"
              >
                <span className="text-3xl" aria-hidden="true">
                  📋
                </span>
                <span className="text-xs font-bold text-[#7C3AED] text-center leading-tight">
                  Check Scorecard
                </span>
              </button>

              <button
                onClick={() => openModal("streaks")}
                className="flex flex-col items-center gap-1.5 bg-[#FFF0F3] hover:bg-[#FFE1E7] rounded-2xl py-4 px-2 transition-colors active:scale-95"
              >
                <span className="text-3xl" aria-hidden="true">
                  🔥
                </span>
                <span className="text-xs font-bold text-[#7C3AED] text-center leading-tight">
                  Check Streaks
                </span>
              </button>

              <button
                onClick={() => openModal("progress")}
                className="flex flex-col items-center gap-1.5 bg-[#EEFAF1] hover:bg-[#DFF5E4] rounded-2xl py-4 px-2 transition-colors active:scale-95"
              >
                <span className="text-3xl" aria-hidden="true">
                  📈
                </span>
                <span className="text-xs font-bold text-[#7C3AED] text-center leading-tight">
                  Progress Graph
                </span>
              </button>

              <button
                onClick={() => openModal("leaderboard")}
                className="flex flex-col items-center gap-1.5 bg-[#EEF3FF] hover:bg-[#DFE9FF] rounded-2xl py-4 px-2 transition-colors active:scale-95"
              >
                <span className="text-3xl" aria-hidden="true">
                  🏆
                </span>
                <span className="text-xs font-bold text-[#7C3AED] text-center leading-tight">
                  Leaderboard
                </span>
              </button>
            </div>

            {/* Gumball Bonanza gets its own full-width glowing card — it's the reward feature */}
            <button
              onClick={() => openModal("gumball")}
              className="w-full flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#FF6FB5] via-[#FF8B6B] to-[#FFD43B] rounded-2xl py-4 shadow-lg active:scale-95 transition-transform animate-pulse-slow"
            >
              <span className="text-3xl" aria-hidden="true">
                🍬
              </span>
              <span className="text-base font-extrabold text-white drop-shadow-sm">
                Gumball Bonanza!
              </span>
            </button>
          </div>
          <button
            onClick={onStart}
            disabled={loading || todayCompleted}
            className="w-full bg-[#FF8B6B] hover:bg-[#F97A57] disabled:opacity-50 text-white font-bold rounded-2xl py-3.5 text-base sm:text-lg shadow-sm transition-colors"
          >
            {loading ? "Loading…" : "▶ Start Today's Play"}
          </button>
          <p className="text-[11px] text-blue-800">
            Play together — about 5 minutes, no rush.
          </p>
        </div>

        {!loading && (
          <ScoreBoard
            dailyTotalScore={history?.[0]?.totalScore || 0}
            streakInfo={streakInfo}
          />
        )}

        <div className="flex justify-center">
          <button
            onClick={() => setShowHistory((s) => !s)}
            className="w-full text-sm font-semibold text-[#7C3AED] hover:text-white bg-white rounded-2xl border border-[#F3EEFF] py-2.5 px-3 flex items-center justify-center gap-2 leading-none"
          >
            <i
              className="fa-solid fa-clock-rotate-left text-sm shrink-0"
              aria-hidden="true"
            ></i>
            <span className="text-center">
              {showHistory ? "Hide Score History" : "View Score History"}
            </span>
          </button>
        </div>
        {showHistory && (
          <div className="mt-2">
            <ScoreHistoryPanel history={history} />
          </div>
        )}
      </main>

      {activeModal && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-auto relative shadow-2xl">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 z-10 w-9 h-9 flex items-center justify-center text-lg lg:text-xl text-gray-400 hover:text-gray-700 transition-colors"
            >
              ✕
            </button>

            {activeModal === "scorecard" && (
              <ScorecardModal
                onClose={closeModal}
                dailyTotalScore={history?.[0]?.totalScore || 0}
                streakInfo={streakInfo}
                history={history}
              />
            )}

            {activeModal === "streaks" && (
              <StreakModal onClose={closeModal} streakInfo={streakInfo} />
            )}

            {activeModal === "progress" && (
              <ProgressGraphModal onClose={closeModal} history={history} />
            )}

            {activeModal === "leaderboard" && (
              <LeaderboardModal
                onClose={closeModal}
                fetchLeaderboard={fetchLeaderboard}
                leaderboard={leaderboard}
              />
            )}

            {activeModal === "gumball" && (
              <Gumball
                onClose={closeModal}
                history={history}
                streakInfo={streakInfo}
              />
            )}

            {activeModal === "rewardhistory" && (
              <GumballHistoryPage onClose={closeModal} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
